/**
 * <s-favorites> — renders the visitor's favorites list inside the favorites drawer.
 * Product data is fetched from the storefront product JSON endpoint each time the
 * list changes or the drawer opens.
 *
 * Attributes:
 *   data-products-url — storefront products base path (from Liquid routes), ends with "/products"
 *   data-currency     — ISO currency code for price formatting
 *   data-locale       — locale for price formatting
 *   data-label-remove — accessible label for the remove buttons, containing [title]
 *   data-image-width  — optional image width to request (default 240)
 *
 * Required child markup:
 *   [data-list]        — <ul> that receives the rendered items
 *   [data-empty]       — empty-state element, hidden while items exist
 *   template[data-item] — item template with [data-link], [data-image], [data-title], [data-price], [data-remove]
 *
 * Commands (via commandfor/command on remove buttons):
 *   --remove-favorite — button value is the product handle
 */
import { getFavorites, removeFavorite, onFavoritesChange } from '@theme/favorites-store';

class SFavorites extends HTMLElement {
  #ac;
  #cache = new Map();
  #renderId = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('command', (event) => {
      if (event.command === '--remove-favorite' && event.source?.value) removeFavorite(event.source.value);
    }, { signal });
    onFavoritesChange(() => this.#render(), signal);
    this.closest('dialog')?.addEventListener('toggle', (event) => {
      if (event.newState === 'open') this.#render();
    }, { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #fetchProduct(handle) {
    if (this.#cache.has(handle)) return this.#cache.get(handle);
    const request = fetch(`${this.dataset.productsUrl}/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    this.#cache.set(handle, request);
    return request;
  }

  #formatPrice(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  async #render() {
    const renderId = ++this.#renderId;
    const list = this.querySelector('[data-list]');
    const empty = this.querySelector('[data-empty]');
    const template = this.querySelector('template[data-item]');
    if (!list || !template) return;

    const handles = getFavorites();
    const products = (await Promise.all(handles.map((handle) => this.#fetchProduct(handle)))).filter(Boolean);
    if (renderId !== this.#renderId) return;

    list.replaceChildren(...products.map((product) => {
      const item = template.content.firstElementChild.cloneNode(true);
      const link = item.querySelector('[data-link]');
      const image = item.querySelector('[data-image]');
      const title = item.querySelector('[data-title]');
      const price = item.querySelector('[data-price]');
      const remove = item.querySelector('[data-remove]');
      if (link) link.href = product.url;
      item.querySelectorAll('[data-link-secondary]').forEach((a) => { a.href = product.url; });
      if (image) {
        const src = product.featured_image ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=${this.dataset.imageWidth || 240}` : '';
        // Square or landscape photos (jewellery) are shown whole on white instead of being cropped to 3:4.
        image.addEventListener('load', () => {
          const isWide = image.naturalHeight > 0 && image.naturalWidth / image.naturalHeight > 0.85;
          image.classList.toggle('object-contain!', isWide);
          image.classList.toggle('bg-white', isWide);
        }, { once: true });
        if (src) image.src = src; else image.remove();
        image.alt = '';
      }
      if (title) title.textContent = product.title;
      if (price) price.textContent = this.#formatPrice(product.price);
      if (remove) {
        remove.value = product.handle;
        if (this.dataset.labelRemove) remove.setAttribute('aria-label', this.dataset.labelRemove.replace('[title]', product.title));
      }
      return item;
    }));
    if (empty) empty.hidden = products.length > 0;
    list.hidden = products.length === 0;
  }
}

if (!customElements.get('s-favorites')) customElements.define('s-favorites', SFavorites);
